'use client';

import Button from '../Button';
import { twMerge } from 'tailwind-merge';
import { FullListing } from '@th/shared/src/types/FullListing';
import useCreateWatchedSearch from '@th/frontend-shared/src/hooks/mutations/useCreateWatchedSearch';
import { useUser } from '../../contexts/UserContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import useFetchWatchedNeighborhood from '@th/frontend-shared/src/hooks/queries/useFetchWatchedNeighborhood';
import { useQueryClient } from '@th/react-query';
import 'react-loading-skeleton/dist/skeleton.css';
import Skeleton from 'react-loading-skeleton';
import { WATCHED_NEIGHBOURHOOD_DEFAULT_SUBSCRIBED_STATUSES } from '@th/shared/src/constants/savedWatches';
import useDeleteWatchedSearch from '@th/frontend-shared/src/hooks/mutations/useDeleteWatchedSearch';
import Loader from '../Loader';
import SvgIcon from '../icons/SvgIcon';

type Props = {
  listing: FullListing;
  className?: string;
};

export default function WatchedNeighborhoodCard({ listing, className }: Props) {
  const { user, isLoggedIn } = useUser();
  const { openAuthModal } = useAuthModal();
  const queryClient = useQueryClient();
  const {
    mutateAsync: createWatchedNeighborhood,
    isLoading: isMutationLoading,
  } = useCreateWatchedSearch({
    onSuccess: () => {
      queryClient.invalidateQueries(['fetchWatchedNeighborhood', listing.id]);
    },
  });

  const {
    watchedNeighborhood,
    isWatchedNeighborhoodActive,
    isEnabledAndFetching: isQueryLoading,
  } = useFetchWatchedNeighborhood(user?.id, listing);

  const { mutateAsync: deleteWatchedSearch, isLoading: isDeleteLoading } =
    useDeleteWatchedSearch({
      onSuccess: () => {
        queryClient.invalidateQueries(['fetchWatchedNeighborhood', listing.id]);
      },
    });

  const neighborhoodName = listing.address.neighborhood;

  function attemptCreateWatchedNeighborhood() {
    if (!isLoggedIn) {
      openAuthModal();
    } else {
      createWatchedNeighborhood({
        userId: user.id,
        label: `${neighborhoodName} - ${listing.propertyTypeText()}`,
        searchFilters: {
          propertyTypes: listing.propertyTypesFilterOptions(),
          neighborhoodName,
          transactionType: listing.transactionType,
        },
        subscribedStatuses: WATCHED_NEIGHBOURHOOD_DEFAULT_SUBSCRIBED_STATUSES,
      });
    }
  }

  return (
    <div
      className={twMerge(
        'flex flex-row items-center p-4 border rounded-lg shadow-2xl ',
        isWatchedNeighborhoodActive ? 'bg-green-100' : 'bg-white',
        isQueryLoading && 'bg-gray-50',
        className,
      )}
    >
      <div className="mr-6 ml-2">
        <SvgIcon name={'FaBell'} />
      </div>

      {isQueryLoading ? (
        <div className="flex-1">
          <Skeleton count={2} />
        </div>
      ) : (
        <>
          {isWatchedNeighborhoodActive ? (
            <>
              <p className="text-start">
                Receiving updates for {listing.propertyTypeText()} homes in{' '}
                {neighborhoodName}, {listing.address.city}
              </p>
              <button
                onClick={() => {
                  deleteWatchedSearch(watchedNeighborhood.id);
                }}
                className="p-2"
                disabled={isDeleteLoading}
              >
                {isDeleteLoading ? <Loader /> : <SvgIcon name={'HiX'} />}
              </button>
            </>
          ) : (
            <>
              <p className="text-start">
                Receive updates for{' '}
                <strong>{listing.propertyTypeText()}</strong> homes in{' '}
                <strong>{neighborhoodName}</strong>, {listing.address.city}
              </p>
              <Button
                onClick={attemptCreateWatchedNeighborhood}
                title={'Watch'}
                isLoading={isMutationLoading}
                buttonClassName={'ml-3 bg-white border border-darkGray'}
                textClassName={'text-darkGray'}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
